import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { BarChart3, List, Home, Plus, Users, LogOut, User, Menu, X } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';
import { AuthProvider } from './contexts/AuthContext';
import Dashboard from './components/Dashboard';
import SalesTable from './components/SalesTable';
import SalesForm from './components/SalesForm';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import FarmerManagement from './components/FarmerManagement';
import FlopManagement from './components/FlopManagement';
import FarmRecords from './components/FarmRecords';

function Navigation() {
  const location = useLocation();
  const { currentUser, userRole, logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home, requiresAuth: true },
    { path: '/sales', label: 'Sales Records', icon: List, requiresAuth: true },
    { path: '/farm-records', label: 'Farm Records', icon: List, requiresAuth: true },
    { path: '/users', label: 'User Management', icon: Users, requiresAuth: true, requiresAdmin: true },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Debug information
  console.log('Navigation Debug:', {
    currentUser: !!currentUser,
    userRole,
    isAdmin: isAdmin(),
    location: location.pathname
  });

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <img src="/logo.png" alt="Poultry Farm Logo" className="h-8 w-8" />
              <span className="ml-2 text-xl font-bold text-gray-900">Poultry Farm</span>
            </div>
            {/* Desktop Navigation */}
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navItems.map((item) => {
                // Show all authenticated users Dashboard and Sales Records
                if (item.requiresAuth && !currentUser) return null;
                // Only show User Management to admins
                if (item.requiresAdmin && !isAdmin()) return null;
                
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActive
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-1" />
                    {item.label}
                  </Link>
                );
              })}
              {isAdmin && (
                <Link
                  to="/farmers"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    location.pathname === '/farmers'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Farmers
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/flops"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    location.pathname === '/flops'
                      ? 'border-blue-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  <Users className="h-4 w-4 mr-1" />
                  Flops
                </Link>
              )}
            </div>
          </div>
          
          {/* Desktop User Menu */}
          {currentUser && (
            <div className="hidden sm:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {currentUser.photoURL ? (
                  <img
                    className="h-8 w-8 rounded-full object-cover"
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || currentUser.email}
                    onError={(e) => {
                      console.log('Image failed to load:', currentUser.photoURL);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', currentUser.photoURL);
                    }}
                  />
                ) : null}
                <div className={`h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center ${currentUser.photoURL ? 'hidden' : ''}`}>
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-900">
                    {currentUser.displayName || currentUser.email}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {userRole || 'user'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>
          )}

          {/* Mobile menu button */}
          <div className="sm:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navItems.map((item) => {
              // Show all authenticated users Dashboard and Sales Records
              if (item.requiresAuth && !currentUser) return null;
              // Only show User Management to admins
              if (item.requiresAdmin && !isAdmin()) return null;
              
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center px-3 py-2 text-base font-medium border-l-4 ${
                    isActive
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.label}
                </Link>
              );
            })}
            {isAdmin && (
              <Link
                to="/farmers"
                onClick={closeMobileMenu}
                className={`flex items-center px-3 py-2 text-base font-medium border-l-4 ${
                  location.pathname === '/farmers'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                <Users className="h-5 w-5 mr-3" />
                Farmers
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/flops"
                onClick={closeMobileMenu}
                className={`flex items-center px-3 py-2 text-base font-medium border-l-4 ${
                  location.pathname === '/flops'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
                }`}
              >
                <Users className="h-5 w-5 mr-3" />
                Flops
              </Link>
            )}
          </div>
          
          {/* Mobile user menu */}
          {currentUser && (
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center px-4">
                {currentUser.photoURL ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={currentUser.photoURL}
                    alt={currentUser.displayName || currentUser.email}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center ${currentUser.photoURL ? 'hidden' : ''}`}>
                  <User className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {currentUser.displayName || currentUser.email}
                  </div>
                  <div className="text-sm text-gray-500 capitalize">
                    {userRole || 'user'}
                  </div>
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

function ProtectedRoute({ children, requireAdmin = false }) {
  const { currentUser, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && !isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-lg font-medium text-gray-900 mb-2">Access Denied</div>
          <p className="text-gray-500">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
}

function SalesPage() {
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAddNew = () => {
    setEditingSale(null);
    setShowForm(true);
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSale(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SalesTable 
        key={refreshKey}
        onEdit={handleEdit} 
        onAddNew={handleAddNew} 
      />
      
      {showForm && (
        <SalesForm
          salesData={editingSale}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}

function AppRoutes() {
  const { currentUser, userRole, isAdmin, loading } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50">
      {currentUser && <Navigation />}
      <main>
        <Routes>
          <Route path="/login" element={
            currentUser ? <Navigate to="/" /> : <Login />
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Dashboard />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute>
              <SalesPage />
            </ProtectedRoute>
          } />
          <Route path="/farm-records" element={
            <ProtectedRoute>
              <FarmRecords />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute requireAdmin>
              <UserManagement />
            </ProtectedRoute>
          } />
          {isAdmin && (
            <Route path="/farmers" element={<FarmerManagement />} />
          )}
          {isAdmin && (
            <Route path="/flops" element={<FlopManagement />} />
          )}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
